import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Badge, Button, Card, Col, Row, Spinner, Table } from "react-bootstrap";
import BreadCrumbs from "../../../components/BreadCrumbs";
import { useFirebaseOrderById } from "../../../hooks/useFirebaseOrders";
import { formatFirestoreTimestamp } from "../../../utility/Utility";

type ActivityItem = {
  status?: string;
  updatedAt?: any;
  reason?: string;
  isActive?: boolean;
  updatedBy?: {
    displayName?: string;
    userId?: string;
    userType?: string;
  };
};

const toTimestampNumber = (value: any): number => {
  if (!value) return 0;
  if (typeof value === "object" && value.seconds) {
    return value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1000000);
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const statusLabel = (value?: string): string => {
  if (!value) return "N/A";
  return value.replace(/([A-Z])/g, " $1").trim();
};

const getUserText = (item: ActivityItem): string => {
  if (item?.updatedBy?.displayName) return item.updatedBy.displayName;
  if (item?.updatedBy?.userId) return item.updatedBy.userId;
  return "System";
};

const normalizeActivities = (
  items: ActivityItem[] | undefined,
  fallbackStatus: string | undefined,
  fallbackTime: any,
): ActivityItem[] => {
  const source = Array.isArray(items) ? [...items] : [];

  if (source.length === 0 && fallbackStatus) {
    return [
      {
        status: fallbackStatus,
        updatedAt: fallbackTime,
        isActive: true,
        reason: "",
      },
    ];
  }

  return source
    .map((item) => ({ ...item }))
    .sort((a, b) => toTimestampNumber(a.updatedAt) - toTimestampNumber(b.updatedAt));
};

const OrderActivityView: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const orderIdFromState = location.state?.orderId;
  const orderIdFromQuery = new URLSearchParams(location.search).get("orderId") || "";
  const orderId = orderIdFromState || orderIdFromQuery;

  const { data: orderData, loading, error } = useFirebaseOrderById(orderId || "");

  const orderStatusActivities = useMemo(() => {
    return normalizeActivities(
      orderData?.orderStatuses,
      orderData?.orderStatus,
      orderData?.updatedAt || orderData?.createdAt,
    );
  }, [orderData]);

  const deliveryStatusActivities = useMemo(() => {
    return normalizeActivities(
      orderData?.deliveryStatuses,
      orderData?.deliveryStatus,
      orderData?.updatedAt || orderData?.createdAt,
    );
  }, [orderData]);

  const handleBack = () => {
    const fromPath = location.state?.fromPath;
    if (fromPath) {
      navigate(fromPath, { state: { orderId } });
      return;
    }

    if (orderData?.isDeliveryOrder) {
      navigate("/orders/delivery-order/view", { state: { orderId } });
      return;
    }

    navigate("/orders/pickup-orders/view", { state: { orderId } });
  };

  return (
    <>
      <BreadCrumbs hasSubMenus={true} />
      <div style={{ marginTop: "2%" }}>
        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap mb-3">
          <h5 className="mb-0">
            Order Activity ({orderData?.orderReference || orderId || "Unknown Order"})
          </h5>
          <Button variant="outline-primary" onClick={handleBack}>
            Back To Order
          </Button>
        </div>

        {!orderId && (
          <Alert variant="warning">Order ID is missing. Please open this page from an Order View.</Alert>
        )}

        {loading && (
          <div className="d-flex align-items-center gap-2 py-3">
            <Spinner animation="border" />
            <span>Loading order activity...</span>
          </div>
        )}

        {!loading && error && (
          <Alert variant="danger">Failed to load order activity. Please try again.</Alert>
        )}

        {!loading && !error && orderData && (
          <>
            <Card className="mb-3">
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <div className="d-flex flex-column gap-2">
                      <span className="text-muted fw-semibold" style={{ fontSize: 14 }}>Current Order Status</span>
                      <Badge bg="primary">{statusLabel(orderData?.orderStatus)}</Badge>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="d-flex flex-column gap-2">
                      <span className="text-muted fw-semibold" style={{ fontSize: 14 }}>Current Delivery Status</span>
                      <Badge bg={orderData?.isDeliveryOrder ? "success" : "secondary"}>
                        {orderData?.isDeliveryOrder
                          ? statusLabel(orderData?.deliveryStatus)
                          : "Not a delivery order"}
                      </Badge>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="d-flex flex-column gap-2">
                      <span className="text-muted fw-semibold" style={{ fontSize: 14 }}>Order Type</span>
                      <Badge bg={orderData?.isDeliveryOrder ? "info" : "dark"}>
                        {orderData?.isDeliveryOrder ? "Delivery" : "Pickup"}
                      </Badge>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header className="bg-light fw-bold">Order Status Activity</Card.Header>
              <Card.Body>
                <Table bordered hover responsive className="mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Status</th>
                      <th>Updated At</th>
                      <th>Updated By</th>
                      <th>Reason</th>
                      <th>Current</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderStatusActivities.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center">No order status activity found</td>
                      </tr>
                    ) : (
                      orderStatusActivities.map((item, index) => (
                        <tr key={`order-status-${index}`}>
                          <td>{index + 1}</td>
                          <td>{statusLabel(item.status)}</td>
                          <td>{formatFirestoreTimestamp(item.updatedAt)}</td>
                          <td>{getUserText(item)}</td>
                          <td>{item.reason || "-"}</td>
                          <td>
                            {item.isActive ? (
                              <Badge bg="success">Active</Badge>
                            ) : (
                              <Badge bg="secondary">Inactive</Badge>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>

            {orderData?.isDeliveryOrder ? (
              <Card>
                <Card.Header className="bg-light fw-bold">Delivery Status Activity</Card.Header>
                <Card.Body>
                  <Table bordered hover responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Status</th>
                        <th>Updated At</th>
                        <th>Updated By</th>
                        <th>Reason</th>
                        <th>Current</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deliveryStatusActivities.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center">No delivery status activity found</td>
                        </tr>
                      ) : (
                        deliveryStatusActivities.map((item, index) => (
                          <tr key={`delivery-status-${index}`}>
                            <td>{index + 1}</td>
                            <td>{statusLabel(item.status)}</td>
                            <td>{formatFirestoreTimestamp(item.updatedAt)}</td>
                            <td>{getUserText(item)}</td>
                            <td>{item.reason || "-"}</td>
                            <td>
                              {item.isActive ? (
                                <Badge bg="success">Active</Badge>
                              ) : (
                                <Badge bg="secondary">Inactive</Badge>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            ) : (
              <Alert variant="info" className="mb-0">
                This is a pickup order. Delivery status activity is not available.
              </Alert>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default OrderActivityView;
